import type { RouteLocationNamedRaw } from 'vue-router'
import { axiosInstance } from '@/api/api'
import { hasErrorCode } from '@/composables/dialogs/dialog-server-errors'
import { carrierPortalPages } from '@/router/carrier-portal-pages'
import { useCarrierBlockedStore } from '@/stores/carrier-blocked'
import {
  CARRIER_BOOKING_LINK_EDIT_ROUTE_NAME,
  CARRIER_BOOKING_LINK_ROUTE_NAME,
  CarrierHashBookingRouteName,
} from '@/utils/carrier.utils'

export const CARRIER_BLOCKED_ERROR_CODE = 'carrier_blocked'
export const TIME_SLOTS_DISABLED_ERROR_CODE = 'time_slots_disabled'

// Not in utils/carrier-portal.ts — that would create a router → guards → utils/carrier-portal → router cycle.
export function isCarrierBlockedError(error: unknown): boolean {
  return hasErrorCode(error, CARRIER_BLOCKED_ERROR_CODE)
}

export function isTimeSlotsDisabledError(error: unknown): boolean {
  return hasErrorCode(error, TIME_SLOTS_DISABLED_ERROR_CODE)
}

export function isCarrierAccessError(error: unknown): boolean {
  return isCarrierBlockedError(error) || isTimeSlotsDisabledError(error)
}

const carrierHashBookingPages: string[] = Object.values(CarrierHashBookingRouteName)

// Surfaces that are themselves inaccessible to blocked carriers. Routes whose chrome is shared
// with open surfaces (privacy/unsubscribe preferences) must never be listed — blocked carriers
// have to stay able to unsubscribe there. Booking link pages are not listed either: the shipper
// invited the carrier to book, so blocking never gates a booking link.
const carrierBlockedRedirectPages: string[] = [
  ...carrierPortalPages,
  'carrier-chash-auction-hash',
  ...carrierHashBookingPages,
  'carrier-verification',
]

// Booking surfaces only: the auction page and the portal lists call no quota-gated endpoint and
// must keep rendering when one shipper's time slots module is off.
const carrierBookingRedirectPages: string[] = [
  ...carrierHashBookingPages,
  CARRIER_BOOKING_LINK_ROUTE_NAME,
  CARRIER_BOOKING_LINK_EDIT_ROUTE_NAME,
  'carrier-booking-hash',
  'carrier-booking-edit-hash',
]

type CarrierAccessRule = {
  matches: (error: unknown) => boolean
  pages: string[]
  to: RouteLocationNamedRaw
}

const CARRIER_ACCESS_RULES: readonly CarrierAccessRule[] = [
  { matches: isCarrierBlockedError, pages: carrierBlockedRedirectPages, to: { name: 'carrier-blocked' } },
  { matches: isTimeSlotsDisabledError, pages: carrierBookingRedirectPages, to: { name: 'carrier-bookings-disabled' } },
]

export function carrierAccessRedirect(error: unknown): RouteLocationNamedRaw | null {
  return CARRIER_ACCESS_RULES.find(rule => rule.matches(error))?.to ?? null
}

// Catches carrier access lost mid-session, after the route guards already let the carrier in.
export function useCarrierAccessRedirect(): void {
  const router = useRouter()
  const store = useCarrierBlockedStore()

  async function redirectTo(to: RouteLocationNamedRaw): Promise<void> {
    store.startRedirect()
    try {
      await router.replace(to)
    }
    catch {
      // A failed redirect must not surface as an unhandled rejection from the interceptor.
    }
    finally {
      store.finishRedirect()
    }
  }

  const interceptorId = axiosInstance.interceptors.response.use(undefined, (error) => {
    const routeName = router.currentRoute.value.name

    if (!store.redirectInFlight && typeof routeName === 'string') {
      const rule = CARRIER_ACCESS_RULES.find(({ matches, pages }) => matches(error) && pages.includes(routeName))

      if (rule)
        void redirectTo(rule.to)
    }

    return Promise.reject(error)
  })

  onUnmounted(() => {
    axiosInstance.interceptors.response.eject(interceptorId)
  })
}
